//-------------------------------------------------------------------------------------------------- Imports
import { PayloadAction, createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import {
    createInternetSeguroUser as createInternetSeguroUserMethod,
    deleteInternetSeguroUser as deleteInternetSeguroUserMethod,
    getInternetSeguroAccount as getInternetSeguroAccountMethod,
    getInternetSeguroCategories as getInternetSeguroCategoriesMethod,
    getInternetSeguroUserDevices as getInternetSeguroUserDevicesMethod,
    getInternetSeguroUserPolicies as getInternetSeguroUserPoliciesMethod,
    hasInternetSeguroProfile,
    scheduleInternetSeguroRule as scheduleInternetSeguroRuleMethod,
    toggleInternetSeguroRules as toggleInternetSeguroRulesMethod,
    updateInternetSeguroUser as updateInternetSeguroUserMethod,
    validateParentalControl,
} from "../../methods/Methods";
import {
    InternetSeguroApiResponse,
    InternetSeguroCategory,
    InternetSeguroDevice,
    InternetSeguroPolicy,
    InternetSeguroSchedule,
    InternetSeguroUser,
    RequestInternetSeguroCreateUser,
    RequestInternetSeguroScheduleRule,
    RequestInternetSeguroToggleRules,
    ResponseInternetSeguroAccountDetail,
    ResponseInternetSeguroCategories,
    ResponseInternetSeguroDevices,
    ResponseInternetSeguroPolicies,
    ResponseValidateParentalControl,
} from "../../types/common";

//-------------------------------------------------------------------------------------------------- Interfaces
export interface InternetSeguroDeviceWithUser {
    device: InternetSeguroDevice;
    user: InternetSeguroUser;
}

export interface InternetSeguroState {
    hasProfile: boolean | null;
    loaded: boolean;
    parentalControlEntitlement: ResponseValidateParentalControl | null;
    parentalControlEntitlementLoaded: boolean;
    account: ResponseInternetSeguroAccountDetail | null;
    accountLoading: boolean;
    accountLoaded: boolean;
    userDevices: Record<string, InternetSeguroDevice[]>;
    userDevicesLoading: Record<string, boolean>;
    userDevicesLoaded: Record<string, boolean>;
    categories: InternetSeguroCategory[] | null;
    categoriesLoading: boolean;
    categoriesLoaded: boolean;
    userPolicies: Record<string, Record<string, InternetSeguroPolicy>>;
    userPoliciesLoading: Record<string, boolean>;
    userPoliciesLoaded: Record<string, boolean>;
}

//-------------------------------------------------------------------------------------------------- Vars and consts
const internetSeguroInitialState: InternetSeguroState = {
    hasProfile: null,
    loaded: false,
    parentalControlEntitlement: null,
    parentalControlEntitlementLoaded: false,
    account: null,
    accountLoading: false,
    accountLoaded: false,
    userDevices: {},
    userDevicesLoading: {},
    userDevicesLoaded: {},
    categories: null,
    categoriesLoading: false,
    categoriesLoaded: false,
    userPolicies: {},
    userPoliciesLoading: {},
    userPoliciesLoaded: {},
};

// Solo con estatus=OK, internetSeguro=1 y controlParental=1 el contrato tiene el servicio activo.
export const isParentalControlEntitlementActive = (
    entitlement: ResponseValidateParentalControl | null
): boolean =>
    entitlement?.estatus === "OK" &&
    entitlement?.internetSeguro === 1 &&
    entitlement?.controlParental === 1;

//-------------------------------------------------------------------------------------------------- Thunks
export const getInternetSeguroProfile = createAsyncThunk(
    "thunk/setInternetSeguroProfile",
    async (_: void, thunkAPI): Promise<boolean | null> => {
        // Se cachea en Redux para toda la sesión: ParentalControlHome.tsx dispara esto
        // en cada montaje, y no queremos volver a pedir hasProfile si ya se cargó
        // en una visita anterior.
        const state = thunkAPI.getState() as any;
        if (state.internetSeguro.loaded) {
            return state.internetSeguro.hasProfile as boolean | null;
        }
        // hasProfile solo tiene sentido si el usuario cuenta con el entitlement de
        // Control Parental activo (se espera que getParentalControlEntitlement ya
        // haya corrido antes, ver ParentalControlHome.tsx).
        const entitlement = state.internetSeguro.parentalControlEntitlement as ResponseValidateParentalControl | null;
        if (!isParentalControlEntitlementActive(entitlement)) {
            return null;
        }
        let hasProfile: boolean | null = null;
        await hasInternetSeguroProfile()
            .then((response: AxiosResponse<Boolean>) => {
                if (response && response.data != null) {
                    hasProfile = Boolean(response.data);
                }
            })
            .catch(() => { });
        return hasProfile;
    }
);

export const getParentalControlEntitlement = createAsyncThunk(
    "thunk/setParentalControlEntitlement",
    async (): Promise<ResponseValidateParentalControl | null> => {
        let entitlement: ResponseValidateParentalControl | null = null;
        await validateParentalControl()
            .then((response: AxiosResponse<any>) => {
                if (response && response.data && response.data.data && response.data.data[0]) {
                    entitlement = response.data.data[0] as ResponseValidateParentalControl;
                }
            })
            .catch(() => { });
        return entitlement;
    }
);

export const getInternetSeguroAccount = createAsyncThunk(
    "thunk/setInternetSeguroAccount",
    async (): Promise<ResponseInternetSeguroAccountDetail | null> => {
        let account: ResponseInternetSeguroAccountDetail | null = null;
        await getInternetSeguroAccountMethod()
            .then((response: AxiosResponse<InternetSeguroApiResponse<ResponseInternetSeguroAccountDetail>>) => {
                if (
                    response &&
                    response.data &&
                    response.data.status === "SUCCESS" &&
                    response.data.data
                ) {
                    account = response.data.data;
                }
            })
            .catch(() => { });
        return account;
    }
);

export const getInternetSeguroUserDevices = createAsyncThunk(
    "thunk/setInternetSeguroUserDevices",
    async (userId: string): Promise<{ userId: string; devices: InternetSeguroDevice[] }> => {
        let devices: InternetSeguroDevice[] = [];
        await getInternetSeguroUserDevicesMethod(userId)
            .then((response: AxiosResponse<InternetSeguroApiResponse<ResponseInternetSeguroDevices>>) => {
                if (
                    response &&
                    response.data &&
                    response.data.status === "SUCCESS" &&
                    response.data.data
                ) {
                    devices = response.data.data.devices ?? [];
                }
            })
            .catch(() => { });
        return { userId, devices };
    }
);

export const getInternetSeguroCategories = createAsyncThunk(
    "thunk/setInternetSeguroCategories",
    async (): Promise<InternetSeguroCategory[]> => {
        let categories: InternetSeguroCategory[] = [];
        await getInternetSeguroCategoriesMethod()
            .then((response: AxiosResponse<InternetSeguroApiResponse<ResponseInternetSeguroCategories>>) => {
                if (
                    response &&
                    response.data &&
                    response.data.status === "SUCCESS" &&
                    response.data.data
                ) {
                    categories = response.data.data.categories ?? [];
                }
            })
            .catch(() => { });
        return categories;
    }
);

export const getInternetSeguroUserPolicies = createAsyncThunk(
    "thunk/setInternetSeguroUserPolicies",
    async (userId: string): Promise<{ userId: string; policies: Record<string, InternetSeguroPolicy> }> => {
        let policies: Record<string, InternetSeguroPolicy> = {};
        await getInternetSeguroUserPoliciesMethod(userId)
            .then((response: AxiosResponse<InternetSeguroApiResponse<ResponseInternetSeguroPolicies>>) => {
                if (
                    response &&
                    response.data &&
                    response.data.status === "SUCCESS" &&
                    response.data.data
                ) {
                    policies = response.data.data.policies ?? {};
                }
            })
            .catch(() => { });
        return { userId, policies };
    }
);

export const createInternetSeguroUserThunk = createAsyncThunk(
    "thunk/createInternetSeguroUser",
    async (body: RequestInternetSeguroCreateUser): Promise<void> => {
        await createInternetSeguroUserMethod(body);
    }
);

export const updateInternetSeguroUserThunk = createAsyncThunk(
    "thunk/updateInternetSeguroUser",
    async ({ userId, body }: { userId: string; body: RequestInternetSeguroCreateUser }): Promise<void> => {
        await updateInternetSeguroUserMethod(userId, body);
    }
);

export const deleteInternetSeguroUserThunk = createAsyncThunk(
    "thunk/deleteInternetSeguroUser",
    async (userId: string): Promise<void> => {
        await deleteInternetSeguroUserMethod(userId);
    }
);

export const toggleInternetSeguroRule = createAsyncThunk(
    "thunk/toggleInternetSeguroRule",
    async ({
        userId,
        categoryCode,
        enabled,
    }: {
        userId: string;
        categoryCode: string;
        enabled: boolean;
    }): Promise<{ userId: string; categoryCode: string; enabled: boolean }> => {
        const body: RequestInternetSeguroToggleRules = {
            policies: [{ categoryCode, enabled }],
        };
        const response = await toggleInternetSeguroRulesMethod(userId, body);
        if (response.data?.status !== "SUCCESS") {
            throw new Error(response.data?.message || "No fue posible actualizar la regla.");
        }
        return { userId, categoryCode, enabled };
    }
);

export const scheduleInternetSeguroRule = createAsyncThunk(
    "thunk/scheduleInternetSeguroRule",
    async ({
        userId,
        categoryCode,
        schedule,
    }: {
        userId: string;
        categoryCode: string;
        schedule: InternetSeguroSchedule;
    }): Promise<{ userId: string; categoryCode: string; schedule: InternetSeguroSchedule }> => {
        const body: RequestInternetSeguroScheduleRule = { categoryCode, schedule };
        const response = await scheduleInternetSeguroRuleMethod(userId, body);
        if (response.data?.status !== "SUCCESS") {
            throw new Error(response.data?.message || "No fue posible programar el horario.");
        }
        return { userId, categoryCode, schedule };
    }
);

//-------------------------------------------------------------------------------------------------- Slice
const internetSeguroSlice = createSlice({
    name: "internetSeguro",
    initialState: internetSeguroInitialState,
    reducers: {
        setInternetSeguroProfile: (state, action: PayloadAction<boolean | null>) => {
            state.hasProfile = action.payload;
            state.loaded = true;
        },
        clearInternetSeguroData: (state) => {
            state.hasProfile = null;
            state.loaded = false;
            state.parentalControlEntitlement = null;
            state.parentalControlEntitlementLoaded = false;
            state.account = null;
            state.accountLoading = false;
            state.accountLoaded = false;
            state.userDevices = {};
            state.userDevicesLoading = {};
            state.userDevicesLoaded = {};
            state.categories = null;
            state.categoriesLoading = false;
            state.categoriesLoaded = false;
            state.userPolicies = {};
            state.userPoliciesLoading = {};
            state.userPoliciesLoaded = {};
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getInternetSeguroProfile.fulfilled, (state, action) => {
            state.hasProfile = action.payload;
            state.loaded = true;
        });
        builder.addCase(getParentalControlEntitlement.fulfilled, (state, action) => {
            state.parentalControlEntitlement = action.payload;
            state.parentalControlEntitlementLoaded = true;
        });
        builder.addCase(getInternetSeguroAccount.pending, (state) => {
            state.accountLoading = true;
        });
        builder.addCase(getInternetSeguroAccount.fulfilled, (state, action) => {
            state.account = action.payload;
            state.accountLoading = false;
            state.accountLoaded = true;
        });
        builder.addCase(getInternetSeguroAccount.rejected, (state) => {
            state.accountLoading = false;
            state.accountLoaded = true;
        });
        builder.addCase(getInternetSeguroUserDevices.pending, (state, action) => {
            state.userDevicesLoading[action.meta.arg] = true;
        });
        builder.addCase(getInternetSeguroUserDevices.fulfilled, (state, action) => {
            state.userDevices[action.payload.userId] = action.payload.devices;
            state.userDevicesLoading[action.payload.userId] = false;
            state.userDevicesLoaded[action.payload.userId] = true;
        });
        builder.addCase(getInternetSeguroUserDevices.rejected, (state, action) => {
            state.userDevicesLoading[action.meta.arg] = false;
            state.userDevicesLoaded[action.meta.arg] = true;
        });
        builder.addCase(getInternetSeguroCategories.pending, (state) => {
            state.categoriesLoading = true;
        });
        builder.addCase(getInternetSeguroCategories.fulfilled, (state, action) => {
            state.categories = action.payload;
            state.categoriesLoading = false;
            state.categoriesLoaded = true;
        });
        builder.addCase(getInternetSeguroCategories.rejected, (state) => {
            state.categoriesLoading = false;
            state.categoriesLoaded = true;
        });
        builder.addCase(getInternetSeguroUserPolicies.pending, (state, action) => {
            state.userPoliciesLoading[action.meta.arg] = true;
        });
        builder.addCase(getInternetSeguroUserPolicies.fulfilled, (state, action) => {
            state.userPolicies[action.payload.userId] = action.payload.policies;
            state.userPoliciesLoading[action.payload.userId] = false;
            state.userPoliciesLoaded[action.payload.userId] = true;
        });
        builder.addCase(getInternetSeguroUserPolicies.rejected, (state, action) => {
            state.userPoliciesLoading[action.meta.arg] = false;
            state.userPoliciesLoaded[action.meta.arg] = true;
        });
        builder.addCase(toggleInternetSeguroRule.fulfilled, (state, action) => {
            const { userId, categoryCode, enabled } = action.payload;
            const userPolicy = state.userPolicies[userId]?.[categoryCode];
            if (userPolicy) {
                userPolicy.enable = enabled;
            } else {
                state.userPolicies[userId] = {
                    ...state.userPolicies[userId],
                    [categoryCode]: { enable: enabled },
                };
            }
        });
        builder.addCase(scheduleInternetSeguroRule.fulfilled, (state, action) => {
            const { userId, categoryCode, schedule } = action.payload;
            const userPolicy = state.userPolicies[userId]?.[categoryCode];
            if (userPolicy) {
                userPolicy.schedule = schedule;
            } else {
                state.userPolicies[userId] = {
                    ...state.userPolicies[userId],
                    [categoryCode]: { enable: false, schedule },
                };
            }
        });
    },
});

//-------------------------------------------------------------------------------------------------- Exports
export const { setInternetSeguroProfile, clearInternetSeguroData } = internetSeguroSlice.actions;

export const selectHasParentalControlEntitlement = createSelector(
    [
        (state: any) => state.internetSeguro.parentalControlEntitlement as ResponseValidateParentalControl | null,
    ],
    isParentalControlEntitlementActive
);

export const selectInternetSeguroDevices = createSelector(
    [
        (state: any) => state.internetSeguro.account as ResponseInternetSeguroAccountDetail | null,
        (state: any) => state.internetSeguro.userDevices as Record<string, InternetSeguroDevice[]>,
    ],
    (account, userDevices) =>
        (account?.users ?? []).flatMap((user) =>
            (userDevices[user.userId] ?? []).map((device) => ({ device, user }))
        )
);

export default internetSeguroSlice.reducer;